import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './event-schedule.scss';
import eventsData from '../../schedule-items/events/events.json';
import EventCard from '../event-card/event-card';
import EventImageModal from '../event-image-modal/event-image-modal';
import {
  getEventImages,
  hasEventEnded,
  type EventSponsor,
} from '../../helpers/eventHelpers';
import { parseLocalDate } from '../../utils/dateUtils';

export type EventSchedule = {
  id: string;
  active: boolean;
  origin: string;
  title: string;
  startDate: string;
  endDate?: string;
  time?: string;
  location: string;
  description: string;
  link?: string | null;
  image?: string;
  images?: string[];
  sponsors?: EventSponsor[];
};

export const eventEvents: EventSchedule[] = eventsData as EventSchedule[];

const getWeekRange = (date: Date) => {
  const copy = new Date(date);
  const day = copy.getDay();
  const sunday = new Date(copy);
  sunday.setDate(copy.getDate() - day);
  sunday.setHours(0, 0, 0, 0);

  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  saturday.setHours(23, 59, 59, 999);

  return { sunday, saturday };
};

export const getCurrentWeekEvents = (events: EventSchedule[]) => {
  const today = new Date();
  const { sunday, saturday } = getWeekRange(today);

  return events.filter((event) => {
    const eventStart = parseLocalDate(event.startDate);
    const eventEnd = parseLocalDate(event.endDate || event.startDate);
    return eventStart <= saturday && eventEnd >= sunday && !hasEventEnded(event);
  });
};

type EventsScheduleProps = {
  showAll?: boolean;
  enableImageModal?: boolean;
  preferFullImagePreview?: boolean;
  compactHomePreview?: boolean;
};

const EventsSchedule = ({
  showAll = false,
  enableImageModal = false,
  preferFullImagePreview = false,
  compactHomePreview = false,
}: EventsScheduleProps) => {
  const { hash } = useLocation();
  const [imageIndexes, setImageIndexes] = useState<Record<string, number>>({});
  const [selectedImage, setSelectedImage] = useState<{
    images: string[];
    alt: string;
    index: number;
  } | null>(null);

  useEffect(() => {
    if (!selectedImage) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedImage(null);
      }

      if (selectedImage.images.length > 1 && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
        const direction = event.key === 'ArrowLeft' ? -1 : 1;
        setSelectedImage((current) => {
          if (!current) {
            return null;
          }

          return {
            ...current,
            index: (current.index + direction + current.images.length) % current.images.length,
          };
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedImage]);

  useEffect(() => {
    if (!showAll || !hash.startsWith('#event-')) {
      return;
    }

    const scrollToEvent = () => {
      const eventCard = document.getElementById(hash.slice(1));
      if (!eventCard) {
        return;
      }

      const navigationOffset = 85;
      const pageViewport = document.querySelector<HTMLElement>('.page-viewport');
      if (pageViewport) {
        const targetPosition = eventCard.getBoundingClientRect().top - pageViewport.getBoundingClientRect().top + pageViewport.scrollTop - navigationOffset;
        pageViewport.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
        return;
      }

      const targetPosition = eventCard.getBoundingClientRect().top + window.scrollY - navigationOffset;
      window.scrollTo({ top: Math.max(0, targetPosition), behavior: 'smooth' });
    };

    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToEvent);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [hash, showAll]);

  const activeEvents = eventEvents.filter((event) => event.active && !hasEventEnded(event));
  const events = showAll ? activeEvents : getCurrentWeekEvents(activeEvents);
  const blockClassName = `event-block ${
    preferFullImagePreview ? 'event-block--full-preview' : ''
  }`;

  return (
    <div className={blockClassName}>
      {events.length ? (
        <div className="event-list">
          {events.map((event) => {
            const images = getEventImages(event);
            const currentIndex = Math.min(imageIndexes[event.id] ?? 0, Math.max(images.length - 1, 0));

            return (
              <EventCard
                key={event.id}
                event={event}
                images={images}
                currentImage={images[currentIndex]}
                currentIndex={currentIndex}
                showAll={showAll}
                hash={hash}
                enableImageModal={enableImageModal}
                compactHomePreview={compactHomePreview}
                onImageChange={(direction) => {
                  setImageIndexes((current) => ({
                    ...current,
                    [event.id]: (currentIndex + direction + images.length) % images.length,
                  }));
                }}
                onOpenImageViewer={() => setSelectedImage({ images, alt: event.title, index: currentIndex })}
              />
            );
          })}
        </div>
      ) : (
        <div className="event-empty">
          <p>No events scheduled. Please check back later for updates.</p>
        </div>
      )}

      {selectedImage && (
        <EventImageModal
          selectedImage={selectedImage}
          onClose={() => setSelectedImage(null)}
          onChangeIndex={(direction) => {
            setSelectedImage((current) => current && {
              ...current,
              index: (current.index + direction + current.images.length) % current.images.length,
            });
          }}
        />
      )}
    </div>
  );
};

export default EventsSchedule;
